/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-12-06 09:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import {
  Card,
  Pagination,
  TextContainer,
  SkeletonBodyText,
  SkeletonDisplayText
} from '@shopify/polaris';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import history from 'src/routes/history';
import { routes } from 'src/routes';

import NoRules from 'src/components/views/merchandising-v2/no-rules';
import ListRules from 'src/components/views/merchandising-v2/list-rules';
import FiltersBar from 'src/components/views/merchandising-v2/filters-bar';
import ProcessAddon from 'src/components/views/subscription-v3/addon/process-addon';
import ProcessAddonV4 from 'src/components/views/subscription-v4/addon/process-addon';
import PremiumLockCard from 'src/components/views/merchandising-v2/premium-lock-card';
import MerchandisingNodata from 'src/components/views/merchandising-v2/no-data';
import ActionsGroupOnListing from 'src/components/views/merchandising-v2/actions-group-on-listing';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';

import { buildDataUpdateStatusMerchandisingV2 } from 'src/helpers/merchandising-v2-helper';
import { prettyText } from 'src/helpers';
import {
  NUMBER_MERCHANDISING_RULES_PER_PAGE,
  COLUMN_COMMERCE_OPTIONS,
  COLUMN_COLLECTION_OPTIONS
} from 'src/constants/merchandising-v2';
import ExceedFeatureBanner from 'src/components/views/subscription-v4/exceed-feature-banner';

import { ADDONS_INFO, ADDONS_INFO_V4, VISUAL_MERCHANDISING_NAME } from 'src/constants/subscription';
import * as merchandisingV2Actions from 'src/stores/actions/merchandising-v2-actions';
import * as filterDataActions from 'actions/filter-data-actions';
import * as productPerformanceRankingAction from 'actions/product-performance-ranking-action';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';

type Props = {
  getMerchandisingRules: () => void,
  deleteMerchandisingRule: () => void,
  updateMerchandisingV2Rule: () => void
};

type State = {
  queryValue: string,
  selected: number,
  enabledFromDate: string,
  enabledToDate: string,
  skip: number,
  limit: number,
  allMerchandisingRules: Array,
  totalAllRules: number,
  hasPrevious: Boolean,
  changePlanFromReviewIOModal: Boolean,
  hasNext: Boolean
};
type ComposedProps = Props & WithI18nProps;

class MerchandisingTab extends Component<ComposedProps, State> {
  state = {
    // Filters
    queryValue: '',
    status: [],
    enabledFromDate: '',
    enabledToDate: '',
    skip: 0,
    limit: NUMBER_MERCHANDISING_RULES_PER_PAGE,
    hasPrevious: false,
    hasNext: true,
    firstLoad: true,
    planLimitModal: false,
    changePlanFromReviewIOModal: false,

    allMerchandisingRules: [],
    totalAllRules: 0,
    modalAddon: false
  };

  async componentDidMount() {
    const {
      panelID,
      getProductAttribute,
      getMerchandisingRules,
      getSettings,
      enableAISearch,
      flagShowRanking,
      optionsList,
      invoice,
      rankingSettings,
      getDetailInvoice,
      getPackagePlans
    } = this.props;
    const { skip, limit, firstLoad } = this.state;

    // Get detail invoice
    if (_.isEmpty(invoice) || !invoice) {
      getDetailInvoice();
    }

    // Get package plans
    getPackagePlans();

    // Get product performance ranking settings
    if ((_.isEmpty(rankingSettings) || !rankingSettings) && enableAISearch && flagShowRanking) {
      getSettings();
    }
    // Get collection data
    await getMerchandisingRules('', skip, limit, panelID);

    if (_.isEmpty(optionsList)) {
      await getProductAttribute();
    }
    this.pushMerchandisingRulesToList(firstLoad);
  }

  render() {
    const {
      filterDataLoading,
      deleting,
      saving,
      appLanguage,
      totalRules,
      loading,
      panelID,
      success,
      activeRuleLimit,
      isSubscribeNow,
      notAllowAddon,
      rankingSettings,
      enableAISearch,
      enableCollectionPage,
      lockRuleCollectionPage,
      flagShowRanking,
      totalAllUnlockRules,
      enabledSubscriptionV4,
      getAddonStatus,
      useBoltOn,
      i18n
    } = this.props;

    const {
      queryValue,
      status,
      enabledFromDate,
      enabledToDate,
      allMerchandisingRules,
      hasPrevious,
      hasNext,
      firstLoad
    } = this.state;
    const hasCurrentMerchandisingRules = !_.isEmpty(allMerchandisingRules);

    const columns = panelID === 'search' ? COLUMN_COMMERCE_OPTIONS : COLUMN_COLLECTION_OPTIONS;

    const enabledRanking = _.get(rankingSettings, 'enabled', false);
    const showRankingStrategy = enableAISearch && enabledRanking && flagShowRanking;

    const notAllowAddonMerchandising = !enableCollectionPage && panelID === 'collection';

    return (
      <>
        <div style={{ marginBottom: '16px' }}>{this.renderExceedFeatureNotify()}</div>
        <Card>
          {(loading || filterDataLoading) && firstLoad ? (
            <Card.Section>
              <div className="BC-Page BC-Page__Fields">
                <TextContainer>
                  <SkeletonDisplayText size="small" />
                  <SkeletonBodyText />
                </TextContainer>
              </div>
            </Card.Section>
          ) : (
            <>
              {notAllowAddonMerchandising &&
              !(hasCurrentMerchandisingRules && lockRuleCollectionPage) ? (
                <PremiumLockCard
                  disableLockUnLock={notAllowAddon}
                  isSubscribeNow={isSubscribeNow}
                  action={() => {
                    useBoltOn
                      ? this.toggleModal('changePlanFromReviewIOModal')
                      : this.toggleModal('modalAddon');
                  }}
                />
              ) : totalRules || loading || filterDataLoading ? (
                <>
                  <Card.Section>
                    {panelID === 'search' && (
                      <p style={{ marginBottom: '0.5rem' }}>
                        {i18n.translate('AdminHTML.MerchandisingV2.Common.Description')}
                      </p>
                    )}
                    <FiltersBar
                      queryValue={queryValue}
                      status={status}
                      enabledFromDate={enabledFromDate}
                      enabledToDate={enabledToDate}
                      appLanguage={appLanguage}
                      onQueryChange={this.handleFiltersQueryChange}
                      handleKeyDown={this.handleKeyDown}
                      handleKeyUp={this.handleKeyUp}
                      onChooseDate={this.onChooseDate}
                      handleApplyFilterRule={this.handleApplyFilterRule}
                      handleStatusChange={this.handleStatusChange}
                      panelID={panelID}
                    />
                  </Card.Section>
                  <Card.Section>
                    {hasCurrentMerchandisingRules || loading || filterDataLoading ? (
                      <div className="hasRules">
                        <ListRules
                          key={`list-rules-${panelID}`}
                          columns={columns}
                          loading={loading || filterDataLoading}
                          rules={allMerchandisingRules}
                          handleChangeRuleStatus={this.handleChangeRuleStatus}
                          deleteRule={this.props.deleteMerchandisingRule}
                          handleDuplicate={this.handleDuplicateRule}
                          deleting={deleting}
                          success={success}
                          saving={saving}
                          afterDeleteRule={this.afterDeleteRule}
                          appLanguage={appLanguage}
                          total={totalRules}
                          panelID={panelID}
                          lockRuleCollectionPage={lockRuleCollectionPage}
                          notAllowAddonMerchandising={notAllowAddonMerchandising}
                          showRankingStrategy={showRankingStrategy}
                        />
                      </div>
                    ) : (
                      <div style={{ height: 130 }}>
                        <MerchandisingNodata />
                      </div>
                    )}
                  </Card.Section>
                </>
              ) : (
                <Card.Section>
                  <NoRules
                    panelID={panelID}
                    exceedRule={!!(totalAllUnlockRules >= activeRuleLimit && enabledSubscriptionV4)}
                  />
                </Card.Section>
              )}
            </>
          )}
        </Card>
        {!(
          notAllowAddonMerchandising && !(hasCurrentMerchandisingRules && lockRuleCollectionPage)
        ) && (
          <div className="pagination">
            {(hasPrevious || hasNext) && (
              <Pagination
                hasPrevious={hasPrevious}
                onPrevious={this.getPreviousPage}
                hasNext={hasNext}
                onNext={this.getNextPage}
              />
            )}
            {(totalAllUnlockRules < activeRuleLimit || !enabledSubscriptionV4) && (
              <div className="actionsGroup">
                <ActionsGroupOnListing
                  isNewRule={success && totalRules === 0}
                  total={totalRules}
                  isSubscribeNow={isSubscribeNow}
                  hasAddonMerchandising={enableCollectionPage}
                  getAddonStatus={getAddonStatus}
                  enabledSubscriptionV4={enabledSubscriptionV4}
                />
              </div>
            )}
          </div>
        )}

        {enabledSubscriptionV4 ? (
          <ProcessAddonV4
            openModalReview={this.state.modalAddon}
            addon={ADDONS_INFO_V4.find(e => e.addonName === VISUAL_MERCHANDISING_NAME)}
            hadSubscribed={isSubscribeNow}
            handleAfterSubscribeAddon={this.handleCloseStatus}
            handleReviewModal={() => this.toggleModal('modalAddon')}
          />
        ) : (
          <ProcessAddon
            openModalReview={this.state.modalAddon}
            addon={ADDONS_INFO.find(e => e.addonName === VISUAL_MERCHANDISING_NAME)}
            hadSubscribed={isSubscribeNow}
            handleAfterSubscribeAddon={this.handleCloseStatus}
            handleReviewModal={() => this.toggleModal('modalAddon')}
          />
        )}
        <ChangePlanFromReviewIOModal
          open={this.state.changePlanFromReviewIOModal}
          onClose={() => this.setState({ changePlanFromReviewIOModal: false })}
        />
      </>
    );
  }

  reloadPage = async () => {
    const { skip, queryValue, status, enabledFromDate: start, enabledToDate: end } = this.state;
    const { panelID } = this.props;
    await this.props.getMerchandisingRules(
      encodeURIComponent(queryValue) || '',
      skip,
      NUMBER_MERCHANDISING_RULES_PER_PAGE,
      panelID,
      status,
      start,
      end
    );
    this.pushMerchandisingRulesToList();
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  renderExceedFeatureNotify = () => {
    const { totalAllUnlockRules, activeRuleLimit = 100, i18n, enabledSubscriptionV4 } = this.props;
    if (totalAllUnlockRules < activeRuleLimit || !enabledSubscriptionV4) return null;

    const content = {
      basic: i18n.translate('AdminHTML.MerchandisingV2.Plan.notifyUpgrade01', {
        t1: (
          <span style={{ color: '#D72C0D', fontWeight: '600' }}>
            {i18n.translate('AdminHTML.MerchandisingV2.Plan.basicPlan')}
          </span>
        ),
        t2: <span>10</span>
      }),
      essential: i18n.translate('AdminHTML.MerchandisingV2.Plan.notifyUpgrade01', {
        t1: (
          <span style={{ color: '#D72C0D', fontWeight: '600' }}>
            {i18n.translate('AdminHTML.MerchandisingV2.Plan.essentialPlan')}
          </span>
        ),
        t2: <span>40</span>
      }),
      professional: i18n.translate('AdminHTML.MerchandisingV2.Plan.notifyUpgrade02')
    };
    return (
      <ExceedFeatureBanner
        featureLimit={activeRuleLimit}
        content={content}
        history={history}
        feature="merchandising"
        openPlanLimitModal={this.openPlanLimitModal}
        planLimitModal={this.state.planLimitModal}
      />
    );
  };
  openPlanLimitModal = value => {
    this.setState({
      planLimitModal: value
    });
  };

  // Go to previous page
  getPreviousPage = async () => {
    const {
      skip,
      limit,
      queryValue,
      status,
      enabledFromDate: start,
      enabledToDate: end
    } = this.state;
    const { panelID } = this.props;
    const previousSkip = skip - limit;
    const previousLimit = NUMBER_MERCHANDISING_RULES_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    this.setState({ skip: previousSkip, limit: previousLimit });
    await this.props.getMerchandisingRules(
      encodeURIComponent(queryValue) || '',
      previousSkip,
      NUMBER_MERCHANDISING_RULES_PER_PAGE,
      panelID,
      status,
      start,
      end
    );
    this.pushMerchandisingRulesToList();
  };

  // Go to next page
  getNextPage = async () => {
    const {
      skip,
      limit,
      queryValue,
      status,
      enabledFromDate: start,
      enabledToDate: end
    } = this.state;
    const { total, panelID } = this.props;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    this.setState({ skip: nextSkip, limit });
    await this.props.getMerchandisingRules(
      encodeURIComponent(queryValue) || '',
      nextSkip,
      NUMBER_MERCHANDISING_RULES_PER_PAGE,
      panelID,
      status,
      start,
      end
    );
    this.pushMerchandisingRulesToList();
  };

  // On search text change
  handleFiltersQueryChange = queryValue => {
    this.setState({ queryValue });
  };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.handleSearch();
    }, 500);
  };

  // Search redirects
  handleSearch = async () => {
    const { status, enabledFromDate, enabledToDate } = this.state;
    const { panelID } = this.props;
    let queryValue = this.state.queryValue.toLowerCase().trim();
    queryValue = prettyText(queryValue);

    // Having search text, search merchandising rules
    if (queryValue) {
      // Set limit per page
      this.setState({
        skip: 0,
        limit: NUMBER_MERCHANDISING_RULES_PER_PAGE
      });

      // Init list before searching
      this.setState({ allMerchandisingRules: [] });

      await this.props.getMerchandisingRules(
        encodeURIComponent(queryValue),
        0,
        NUMBER_MERCHANDISING_RULES_PER_PAGE,
        panelID,
        status,
        enabledFromDate,
        enabledToDate
      );
      this.pushMerchandisingRulesToList();
    } else {
      // Search text is empty, get all filter tree for page 1
      const skip = 0,
        limit = NUMBER_MERCHANDISING_RULES_PER_PAGE;

      // Set skip, limit for page 1
      this.setState({ skip, limit });

      // Get rules
      await this.props.getMerchandisingRules(
        '',
        skip,
        limit,
        panelID,
        status,
        enabledFromDate,
        enabledToDate
      );
      this.pushMerchandisingRulesToList();
    }
  };

  // Handle choosing date in date filter
  onChooseDate = async selectedDates => {
    const { status } = this.state;
    const { panelID } = this.props;
    let queryValue = this.state.queryValue.toLowerCase().trim();
    queryValue = prettyText(queryValue);

    const { start = '', end = '' } = selectedDates;
    this.setState({
      enabledFromDate: start,
      enabledToDate: end,
      skip: 0,
      limit: NUMBER_MERCHANDISING_RULES_PER_PAGE
    });
    // Get rules
    await this.props.getMerchandisingRules(
      encodeURIComponent(queryValue) || '',
      0,
      NUMBER_MERCHANDISING_RULES_PER_PAGE,
      panelID,
      status,
      start,
      end
    );
    this.pushMerchandisingRulesToList();
  };

  // Handle Apply filter rule
  handleApplyFilterRule = async (selectedDates, status) => {
    const { start = '', end = '' } = selectedDates;
    const { panelID } = this.props;
    let queryValue = this.state.queryValue.toLowerCase().trim();
    queryValue = prettyText(queryValue);

    this.setState({
      status,
      enabledFromDate: start,
      enabledToDate: end,
      skip: 0,
      limit: NUMBER_MERCHANDISING_RULES_PER_PAGE
    });
    // Get rules
    await this.props.getMerchandisingRules(
      encodeURIComponent(queryValue) || '',
      0,
      NUMBER_MERCHANDISING_RULES_PER_PAGE,
      panelID,
      status,
      start,
      end
    );
    this.pushMerchandisingRulesToList();
  };

  // Handle choosing status in status filter
  handleStatusChange = async value => {
    const { enabledFromDate, enabledToDate } = this.state;
    const { panelID } = this.props;
    let queryValue = this.state.queryValue.toLowerCase().trim();
    queryValue = prettyText(queryValue);

    this.setState({
      status: value,
      skip: 0,
      limit: NUMBER_MERCHANDISING_RULES_PER_PAGE
    });

    // Get rules
    await this.props.getMerchandisingRules(
      encodeURIComponent(queryValue) || '',
      0,
      NUMBER_MERCHANDISING_RULES_PER_PAGE,
      panelID,
      value,
      enabledFromDate,
      enabledToDate
    );
    this.pushMerchandisingRulesToList();
  };

  // Push merchandising rules to list every time after reading from DB
  pushMerchandisingRulesToList = (firstLoad: boolean = false) => {
    let { skip, limit, allMerchandisingRules, hasNext, hasPrevious, totalAllRules } = this.state;

    const { total, merchandisingRules = [], totalRules } = this.props;

    hasNext = skip + limit < total;
    hasPrevious = skip > 0;

    // Init list of merchandising for first page
    if (skip === 0) {
      allMerchandisingRules = [];
    }

    if (firstLoad) {
      totalAllRules = totalRules;
    }
    // Merge new rules
    allMerchandisingRules = merchandisingRules;
    this.setState({ allMerchandisingRules, hasNext, hasPrevious, totalAllRules, firstLoad: false });
  };

  // Enable/disable merchandising rule
  handleChangeRuleStatus = async ruleId => {
    const { allMerchandisingRules } = this.state;
    const { updateMerchandisingRule } = this.props;

    // Update rule status
    let rule = _.find(allMerchandisingRules, { id: ruleId });
    rule.status = rule.status === 'disabled' ? 'enabled' : 'disabled';

    // Change status

    const saveData = buildDataUpdateStatusMerchandisingV2(rule, rule.status);
    const ruleUpdatedPayload = await updateMerchandisingRule(ruleId, saveData, 'changeStatus');
    const ruleUpdated = _.get(ruleUpdatedPayload, 'payload.data.data', {});
    // Update status rule in list => update list merchandising rule with data updated
    if (!_.isEmpty(ruleUpdated)) {
      // Update existing rule
      const findIndexUpdated = allMerchandisingRules.findIndex(x => x.id === ruleUpdated.id);
      allMerchandisingRules.splice(findIndexUpdated, 1);
      allMerchandisingRules.unshift(ruleUpdated);
    }

    // this.checkListRules(allMerchandisingRules);
    this.setState({ allMerchandisingRules });
  };

  // Update list rule after update rule status
  checkListRules = (allMerchandisingRules = []) => {
    const { merchandisingRules = [] } = this.props;
    // In case has loadMore while update is calling => update list rule has data loadMore
    const firstIdRule = _.get(merchandisingRules, '[0].id', null);
    if (_.isEmpty(allMerchandisingRules.find(e => e.id === firstIdRule))) {
      allMerchandisingRules = _.concat(allMerchandisingRules, merchandisingRules);
    }
    this.setState({ allMerchandisingRules });
  };

  // Post process after delete rule
  afterDeleteRule = ruleId => {
    let { allMerchandisingRules, totalAllRules } = this.state;

    const newData = _.reject(allMerchandisingRules, { id: ruleId });
    totalAllRules -= 1;
    this.setState({ allMerchandisingRules: newData, totalAllRules });
    if (_.isEmpty(newData)) {
      this.getPreviousPage();
    } else {
      this.reloadPage();
    }
  };

  // Post process after duplicate rule
  handleDuplicateRule = async (ruleId, ruleType) => {
    const { totalAllUnlockRules, activeRuleLimit, enabledSubscriptionV4 } = this.props;
    if (totalAllUnlockRules >= activeRuleLimit && enabledSubscriptionV4) {
      this.openPlanLimitModal(true);
      return;
    }

    history.push({
      pathname: routes.merchandising.new(ruleType),
      search: `?duplicateId=${ruleId}`
    });
  };

  /** Subscribe addon - reload page after subscribe*/
  handleCloseStatus = () => {
    this.props.getAddonStatus(VISUAL_MERCHANDISING_NAME);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSettings: () => dispatch(productPerformanceRankingAction.getSettings()),
  getPackagePlans: () => dispatch(subscriptionV3Actions.getPackagePlans(ownProps.i18n)),
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n)),
  getProductAttribute: () => dispatch(filterDataActions.getProductAttribute(ownProps.i18n)),
  getMerchandisingRules: (
    q,
    skip,
    limit,
    ruleType = 'search',
    status = '',
    enabledFromDate = '',
    enabledToDate = ''
  ) =>
    dispatch(
      merchandisingV2Actions.getMerchandisingV2Rules(
        q,
        skip,
        limit,
        ruleType,
        status,
        enabledFromDate,
        enabledToDate,
        ownProps.i18n
      )
    ),
  updateMerchandisingRule: (ruleId, merchandising, actionName) =>
    dispatch(
      merchandisingV2Actions.updateMerchandisingV2Rule(
        ruleId,
        merchandising,
        [],
        actionName,
        ownProps.i18n
      )
    ),
  clearMerchandisingV2Rule: () => dispatch(merchandisingV2Actions.clearMerchandisingV2Rule()),
  deleteMerchandisingRule: ruleId =>
    dispatch(merchandisingV2Actions.deleteMerchandisingV2Rule(ruleId, ownProps.i18n)),
  isOpenNewVersion: value => dispatch(merchandisingV2Actions.isOpenNewVersion(value))
});

const mapStateToProps = (state, props) => {
  return {
    // number active rule limit is allowed
    activeRuleLimit: _.get(
      state.oauth.tenantApp,
      'settings.visualMerchandising.settings.number-rules-limit',
      100
    ),
    rankingSettings: _.get(state.productPerformanceRanking, 'settings', {}),
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    notAllowAddon: _.get(state.oauth, 'subscription.notAllowAddon', false),
    merchandisingRules: _.get(state.merchandisingV2, 'merchandisingRules', []),
    total: _.get(state.merchandisingV2, 'total', 0),
    totalRules: _.get(state.merchandisingV2, 'totalRules', 0),
    totalAllUnlockRules: _.get(state.merchandisingV2, 'totalAllUnlockRules', 0),
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    loading: _.get(state.merchandisingV2, 'loading', false),
    filterDataLoading: _.get(state.filterData, 'loading', false),
    saving: _.get(state.merchandisingV2, 'saving', false),
    deleting: _.get(state.merchandisingV2, 'deleting', false),
    success: _.get(state.merchandisingV2, 'success', false),
    saveSuccess: _.get(state.merchandisingV2, 'saveSuccess', false),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    enableCollectionPage: _.get(
      state.oauth.tenantApp,
      'settings.visualMerchandising.settings.collection-page',
      false
    ),
    enableAISearch: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.AI-synonyms',
      false
    ),
    enabledSubscriptionV4: _.get(
      state.oauth.tenantApp,
      'settings.subscriptionV4.settings.enable',
      false
    ),
    // Only show and applied ppr for specific store
    flagShowRanking: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.performance-ranking',
      false
    ),
    lockRuleCollectionPage: _.get(
      state.oauth.tenantApp,
      'settings.visualMerchandising.settings.lock-rule-collection-page',
      false
    ),
    optionsList: _.get(state.filterData.productAttributes, 'optionsList', [])
  };
};
export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(MerchandisingTab));
